import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useError } from './ErrorContext';
import {
    getNoticesUrl,
    getNoticePostUrl,
    getNoticePutUrl,
    getNoticeDeleteUrl,
} from '../utils/UrlUtilities';
import { constructNoticePayload } from '../utils/PayloadUtils';
import { NOTICE_TYPES, itemsPerPageOptions, TAGLEVELS, TIMEZONES } from '../utils/Constants';
import { formatPst, isoToZonedInput } from '../utils/DateUtilities';
import { isEmpty } from '../utils/BasicUtilities';

// Flatten TAGLEVELS (including submenu leaves like VARSITY-*/ENGINEERING-*) into level values,
// used to power the Level combobox suggestions.
const LEVEL_OPTIONS = TAGLEVELS.flatMap(item =>
    item.submenu && item.submenu.length ? item.submenu.map(s => s.value) : [item.value]
);

const EMPTY_FORM = { id: null, title: '', body: '', noticeType: 'generic', level: '', isPublished: true, isPinned: false, startsAt: '', endsAt: '' };

// A notice is "active" when it's enabled and currently within its schedule: "Show from" not in
// the future and "Hide after" not in the past. Governs the grid's Active badge.
function isNoticeActive(n) {
    if (!n.is_published) return false;
    const now = new Date();
    if (n.starts_at && new Date(n.starts_at) > now) return false;
    if (n.ends_at && new Date(n.ends_at) < now) return false;
    return true;
}

export default function NoticeManagement() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    const [notices, setNotices] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);

    const [pending, setPending] = useState({ search: '', level: '', date_from: '', date_to: '', is_published: 'true' });
    const [applied, setApplied] = useState({ search: '', level: '', date_from: '', date_to: '', is_published: 'true' });

    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const fetchNotices = useCallback(async () => {
        setLoading(true);
        const params = { page, limit, is_published: applied.is_published };
        if (!isEmpty(applied.search)) params.search = applied.search;
        if (!isEmpty(applied.level)) params.level = applied.level;
        if (!isEmpty(applied.date_from)) params.date_from = applied.date_from;
        if (!isEmpty(applied.date_to)) params.date_to = applied.date_to;
        const res = await CallApi({ url: getNoticesUrl(params), method: 'GET', token: true });
        if (res.success) {
            setNotices(res.data.notices || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [page, limit, applied, CallApi]);

    useEffect(() => { fetchNotices(); }, [fetchNotices]);

    const applyFilters = () => { setApplied(pending); setPage(1); };

    const openCreate = () => { setForm(EMPTY_FORM); setShowForm(true); };
    const openEdit = (n) => {
        setForm({
            id: n.notice_id, title: n.title, body: n.body, noticeType: n.notice_type,
            // is_published is always enabled now — "active" is derived from the Show from / Hide
            // after window, so there's no manual Active toggle in the modal.
            level: n.level, isPublished: true, isPinned: n.is_pinned,
            // Stored as UTC; edit them as PST wall-clock (how they're entered/judged for "active").
            startsAt: isoToZonedInput(n.starts_at, TIMEZONES.PST),
            endsAt: isoToZonedInput(n.ends_at, TIMEZONES.PST),
        });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (isEmpty(form.title)) return;
        setSaving(true);
        const payload = constructNoticePayload({
            title: form.title, body: form.body, noticeType: form.noticeType, level: form.level,
            isPublished: form.isPublished, isPinned: form.isPinned, startsAt: form.startsAt, endsAt: form.endsAt,
        });
        const res = form.id
            ? await CallApi({ url: getNoticePutUrl(form.id), method: 'PUT', payload, token: true })
            : await CallApi({ url: getNoticePostUrl(), method: 'POST', payload, token: true });
        setSaving(false);
        if (res.success) {
            setShowForm(false);
            showSuccess(form.id ? 'Notice updated.' : 'Notice created.');
            fetchNotices();
        }
    };

    const handleDelete = async (n) => {
        if (!window.confirm(`Delete notice "${n.title}"?`)) return;
        const res = await CallApi({ url: getNoticeDeleteUrl(n.notice_id), method: 'DELETE', token: true });
        if (res.success) { showSuccess('Notice deleted.'); fetchNotices(); }
    };

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="fw-bold mb-0" style={{ color: '#4c1d95' }}>Notice Management</h2>
                <button className="btn btn-primary" onClick={openCreate}>+ Add Notice</button>
            </div>

            {/* Filters */}
            <div className="card mb-3 kp-filter"><div className="card-body row g-2 align-items-end">
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Search</label>
                    <input className="form-control form-control-sm" value={pending.search} onChange={(e) => setPending({ ...pending, search: e.target.value })} placeholder="Title or body" />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Level</label>
                    <select className="form-select form-select-sm" value={pending.level} onChange={(e) => setPending({ ...pending, level: e.target.value })}>
                        <option value="">All levels</option>
                        {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">From</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_from} onChange={(e) => setPending({ ...pending, date_from: e.target.value })} />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">To</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_to} onChange={(e) => setPending({ ...pending, date_to: e.target.value })} />
                </div>
                <div className="col-4 col-md-1">
                    <label className="form-label small mb-1">Active</label>
                    <select className="form-select form-select-sm" value={pending.is_published} onChange={(e) => setPending({ ...pending, is_published: e.target.value })}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>
                <div className="col-4 col-md-1">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={applyFilters}>Filter</button>
                </div>
            </div></div>

            {/* Grid */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Title</th><th>Type</th><th>Level</th><th>Active</th><th>Pinned</th><th>Show from</th><th>Hide after</th><th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : notices.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-muted py-4">No notices found.</td></tr>
                        ) : notices.map(n => (
                            <tr key={n.notice_id}>
                                <td>{n.title}</td>
                                <td><span className="badge bg-secondary">{n.notice_type.replace(/_/g, ' ')}</span></td>
                                <td>{n.level || '—'}</td>
                                <td>{isNoticeActive(n) ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</td>
                                <td>{n.is_pinned ? <span className="badge bg-info text-dark">📌 Pinned</span> : '—'}</td>
                                <td className="small text-muted">{n.starts_at ? formatPst(n.starts_at) : '—'}</td>
                                <td className="small text-muted">{n.ends_at ? formatPst(n.ends_at) : '—'}</td>
                                <td className="text-end">
                                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(n)}>Edit</button>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(n)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
            </div>

            {/* Add/Edit modal */}
            {showForm && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(600px, 94vw)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                            {form.id ? 'Edit Notice' : 'Add Notice'}
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Title</label>
                                <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Body</label>
                                <textarea className="form-control" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Type</label>
                                    <select className="form-select" value={form.noticeType} onChange={(e) => setForm({ ...form, noticeType: e.target.value })}>
                                        {NOTICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Level (optional)</label>
                                    <input
                                        className="form-control"
                                        list="notice-level-options"
                                        placeholder="Type a level or pick from the list"
                                        value={form.level}
                                        onChange={(e) => setForm({ ...form, level: e.target.value })}
                                    />
                                    <datalist id="notice-level-options">
                                        {LEVEL_OPTIONS.map(lv => <option key={lv} value={lv} />)}
                                    </datalist>
                                    <small className="form-text text-muted">Leave blank for a global notice (shown to everyone).</small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Show from (PST, optional)</label>
                                    <input type="datetime-local" className="form-control" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
                                    <small className="form-text text-muted">Entered in PST. Leave blank to show immediately.</small>
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label className="form-label fw-semibold">Hide after (PST, optional)</label>
                                    <input type="datetime-local" className="form-control" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
                                    <small className="form-text text-muted">Entered in PST. Leave blank to keep showing indefinitely.</small>
                                </div>
                            </div>
                            <div className="form-check mb-3">
                                <input className="form-check-input" type="checkbox" id="pin" checked={form.isPinned} onChange={(e) => setForm({ ...form, isPinned: e.target.checked })} />
                                <label className="form-check-label" htmlFor="pin">Pinned (always shown, fixed to the left of the board)</label>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSave} disabled={saving || isEmpty(form.title)}>{saving ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
